<template>
  <div class="koneko-a-project-budget-snapshot-panels">
    <div
      class="d-flex flex-wrap align-center mb-2 snapshot-sort-toolbar"
      style="gap: 8px"
    >
      <span class="caption grey--text text--darken-1">Sort by</span>
      <v-btn-toggle
        v-model="sortBy"
        mandatory
        dense
        color="primary"
        @change="persistSortBy"
      >
        <v-btn small value="budgetDelta">Over budget (Δ)</v-btn>
        <v-btn small value="name">Name</v-btn>
      </v-btn-toggle>
    </div>
    <v-alert
      v-if="isFutureCalendarMonth"
      type="info"
      dense
      outlined
      class="mb-2"
    >
      <strong>{{ calendarMonthDisplayTitle || calendarMonthKey }}</strong> has
      not started yet — logged hours use that month’s date range (expect
      <strong>0:00</strong> until timesheets are filed for it).
    </v-alert>
    <p class="caption grey--text text--darken-1 mb-2">
      Bars use <code>konekoAMonthlyProjectBudgets</code> (month key
      <strong>{{ calendarMonthKey }}</strong
      >) · logged hours for <strong>{{ timesheetMonthKey }}</strong> ·
      <span class="font-weight-medium">●</span> = logged ·
      <span class="font-weight-medium">L/H</span> = low/high · scale ends at max
      (hours). Each row shows the bar while collapsed; expand for time by
      member. <strong>Dark red vertical line</strong> = calendar % through the
      month (linear pace). Project row:
      <span class="amber--text text--darken-3 font-weight-medium">amber</span>
      = ahead of that pace line;
      <span class="red--text text--darken-2 font-weight-medium">red</span> =
      over org max (H). <strong>Member bars:</strong> same colours per person.
      <span class="orange--text text--darken-2 font-weight-medium">Orange</span>
      = FLEX (no personal cap).
      <strong>Click a member name</strong> to expand timesheet lines; use
      <strong>Reassign to</strong> (dropdown) to move a line to another project.
      <template v-if="sortBy === 'budgetDelta'">
        <br />
        <span class="mt-1 d-inline-block"
          ><strong>Over budget (Δ)</strong> puts
          <span class="red--text text--darken-2">red</span> (over cap) and
          <span class="amber--text text--darken-3">amber</span> (ahead of pace)
          first, largest gap first; then on-track rows by % of cap used (highest
          first, e.g. 7% last); FLEX last.</span
        >
      </template>
      <template v-if="weekLabel">
        <br />
        <span class="mt-1 d-inline-block"
          >Week of {{ weekLabel }} ({{ monthElapsedPercent }}% through
          month).</span
        >
      </template>
    </p>
    <v-expansion-panels
      v-model="expansionModel"
      multiple
      class="project-budget-snapshot-panels"
    >
      <v-expansion-panel
        v-for="project in sortedRows"
        :key="'snap-' + project.name"
      >
        <v-expansion-panel-header class="snap-project-header py-3 align-start">
          <div
            class="d-flex flex-column flex-grow-1"
            style="min-width: 0; width: 100%"
          >
            <div class="d-flex flex-wrap align-center mb-2" style="gap: 10px">
              <span
                class="subtitle-2 font-weight-medium text-truncate"
                :class="projectTextClass(project)"
                >{{ project.name }}</span
              >
              <span
                v-if="snapshotDeltaLabel(project)"
                class="caption font-weight-medium"
                :class="snapshotDeltaTextClass(project)"
              >
                {{ snapshotDeltaLabel(project) }}
              </span>
              <span
                class="caption grey--text text--darken-1"
                :class="projectTextClass(project)"
              >
                Wk {{ formatHoursCell(project.weekLogged) }}
              </span>
              <span class="caption" :class="projectTextClass(project)"
                >Mo {{ formatHoursCell(project.monthLogged) }}</span
              >
              <span
                class="caption grey--text"
                :class="projectTextClass(project)"
              >
                {{ project.contributors.length }} with time
              </span>
              <span
                v-if="project.snapshotUsageLabel"
                class="caption font-weight-medium"
                :class="projectTextClass(project)"
              >
                {{ project.snapshotUsageLabel }}
              </span>
              <span
                v-if="project.snapshotShowBaselineToggle"
                class="d-inline-flex align-center flex-shrink-0"
                @click.stop
              >
                <span class="caption grey--text text--darken-1 mr-1">% vs</span>
                <v-btn-toggle
                  :value="project.snapshotBaseline"
                  mandatory
                  dense
                  color="primary"
                  @change="(v) => onSnapshotBaselineChange(project.name, v)"
                >
                  <v-btn small value="low">Low</v-btn>
                  <v-btn small value="high">High</v-btn>
                </v-btn-toggle>
              </span>
            </div>
            <div class="snapshot-header-bar-wrap">
              <KonekoBudgetRangeBar
                v-if="project.budgetBar"
                :current-decimal="project.budgetBar.current"
                :low-decimal="project.budgetBar.low"
                :high-decimal="project.budgetBar.high"
                :target-decimal="project.budgetBar.target"
                :month-elapsed-percent="monthElapsedPercent"
                :status="project.barStatus"
              />
              <span v-else class="caption" :class="projectTextClass(project)">{{
                project.budgetStatus || "FLEX"
              }}</span>
            </div>
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="pt-2">
          <div class="caption font-weight-medium mb-2">Time by member</div>
          <KonekoAMemberHoursDrilldownTable
            v-if="project.contributors.length"
            :project-name="project.name"
            :rows="project.contributors"
            :calendar-month-key="calendarMonthKey"
            :month-elapsed-percent="monthElapsedPercent"
            :format-hours-cell="formatHoursCell"
            :project-options="projectOptions"
            :reassign-saving-id="reassignSavingId"
            :hours-saving-id="hoursSavingId"
            :can-reassign-entry="canReassignEntry"
            :can-edit-hours="canEditHours"
            :is-editing-hours="isEditingHours"
            :hours-edit-value="hoursEditValue"
            :show-reassign-ui="showReassignUi"
            @reassign="
              (entry, pid, pname) =>
                $emit('reassign', entry, pid, pname, project.name)
            "
            @edit-hours="$emit('edit-hours', $event)"
            @commit-hours-edit="$emit('commit-hours-edit', $event)"
            @cancel-hours-edit="$emit('cancel-hours-edit')"
            @update:hoursEditValue="$emit('update:hoursEditValue', $event)"
          />
          <p v-else class="caption grey--text text--darken-1 mb-0">
            No member-level rows for this project in the selected month (or
            names did not match the team list).
          </p>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script>
import KonekoBudgetRangeBar from "@/components/Koneko/KonekoBudgetRangeBar.vue";
import KonekoAMemberHoursDrilldownTable from "@/components/Koneko/KonekoAMemberHoursDrilldownTable.vue";
import {konekoBudgetStatusTextClass} from "@/config/konekoAConfig";
import {
  KONEKO_SORT_BUDGET_DELTA,
  KONEKO_SORT_NAME,
  STORAGE_SNAPSHOT_SORT,
  readKonekoSortPref,
  sortSnapshotProjectRows,
  writeKonekoSortPref,
} from "@/utils/konekoASort";

export default {
  name: "KonekoAProjectBudgetSnapshotPanels",
  components: {KonekoBudgetRangeBar, KonekoAMemberHoursDrilldownTable},
  props: {
    rows: {type: Array, default: () => []},
    calendarMonthKey: {type: String, default: ""},
    timesheetMonthKey: {type: String, default: ""},
    snapshotUsesTimesheetFallback: {type: Boolean, default: false},
    isFutureCalendarMonth: {type: Boolean, default: false},
    calendarMonthDisplayTitle: {type: String, default: ""},
    monthElapsedPercent: {type: Number, default: 0},
    weekLabel: {type: String, default: ""},
    formatHoursCell: {type: Function, required: true},
    formatSignedHours: {type: Function, default: null},
    onSnapshotBaselineChange: {type: Function, required: true},
    projectOptions: {type: Array, default: () => []},
    reassignSavingId: {type: [Number, String], default: null},
    hoursSavingId: {type: [Number, String], default: null},
    canReassignEntry: {type: Function, default: () => false},
    canEditHours: {type: Function, default: () => false},
    isEditingHours: {type: Function, default: () => false},
    hoursEditValue: {type: String, default: ""},
    showReassignUi: {type: Boolean, default: false},
  },
  data() {
    return {
      expansionModel: [],
      sortBy: readKonekoSortPref(
        STORAGE_SNAPSHOT_SORT,
        [KONEKO_SORT_NAME, KONEKO_SORT_BUDGET_DELTA],
        KONEKO_SORT_BUDGET_DELTA
      ),
    };
  },
  computed: {
    sortedRows() {
      return sortSnapshotProjectRows(this.rows, this.sortBy);
    },
  },
  methods: {
    persistSortBy() {
      writeKonekoSortPref(STORAGE_SNAPSHOT_SORT, this.sortBy);
    },
    projectTextClass(project) {
      return konekoBudgetStatusTextClass(project.barStatus);
    },
    snapshotDeltaLabel(project) {
      const d = project.snapshotBudgetDeltaHours;
      const pace = project.snapshotPaceAheadHours;
      // Red only when past the hard cap (high / single bound / target).
      if (project.snapshotOverBudget && d != null && d > 0.02) {
        const hrs =
          typeof this.formatSignedHours === "function"
            ? this.formatSignedHours(d)
            : `${d.toFixed(1)}h`;
        return `${hrs} over cap`;
      }
      if (project.snapshotPastCalendarPaceLine && pace != null && pace > 0.02) {
        const hrs =
          typeof this.formatSignedHours === "function"
            ? this.formatSignedHours(pace)
            : `${pace.toFixed(1)}h`;
        return `${hrs} ahead of pace`;
      }
      // Healthy: past the low floor, under the cap, on/behind the pace line.
      if (project.barStatus === "good") return "in budget";
      if (d == null) return "";
      if (Math.abs(d) < 0.02) return "On cap";
      const hrs =
        typeof this.formatSignedHours === "function"
          ? this.formatSignedHours(d)
          : `${d > 0 ? "+" : ""}${d.toFixed(1)}h`;
      return d > 0 ? `${hrs} over cap` : `${hrs} under cap`;
    },
    snapshotDeltaTextClass(project) {
      if (project.snapshotOverBudget) {
        return "red--text text--darken-2 font-weight-medium";
      }
      if (project.snapshotPastCalendarPaceLine) {
        return "amber--text text--darken-3 font-weight-medium";
      }
      if (project.barStatus === "good") {
        return "green--text text--darken-2 font-weight-medium";
      }
      const d = project.snapshotBudgetDeltaHours;
      if (d == null) return "grey--text";
      return "grey--text text--darken-1";
    },
  },
};
</script>

<style scoped>
.snapshot-header-bar-wrap {
  width: 100%;
  max-width: 640px;
}
</style>
